// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import { createdAtField, getCreatedAtSort, idField } from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import Organization from './Organization';
import OrgLocation from './OrgLocation';

class OrganizationUser implements StoredEntity {
    static classInternalName = 'org_user';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        firstName: {
            displayName: 'First Name',
            recordName: 'first_name',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        lastName: {
            displayName: 'Last Name',
            recordName: 'last_name',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        email: {
            displayName: 'Email',
            recordName: 'email',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        role: {
            displayName: 'Role',
            recordName: 'role',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    1: 'Staff',
                    2: 'Admin',
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        organizationId: {
            displayName: 'Organization',
            recordName: 'organization_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Organization,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        locationIds: {
            displayName: 'Locations',
            recordName: 'location_ids',
            dataType: {
                primitive: 'ref',
                isArray: true,
                extraData: {
                    model: () => OrgLocation,
                    maxValues: 5,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        enabled: {
            displayName: 'Enabled',
            recordName: 'enabled',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
    };

    static fileFields: EntityFieldCollection = {};

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[OrganizationUser.fields.id.recordName];
    }

    get displayName() {
        return this.record[OrganizationUser.fields.email.recordName];
    }

    static endpoints: Endpoints = constructEndpoints('organizations/users');

    constructor(record: any) {
        this.record = record;
    }
}

export default OrganizationUser;
